/**
 * Analysis Utility Functions
 *
 * Provides helper functions for:
 * - Matching analysis type names to configurations
 * - Getting status icons and colors
 *
 * Works with the centralized constants from lib/constants/analysis.ts
 */

import {
  ANALYSIS_TYPE_COLORS,
  ANALYSIS_TYPE_CONFIG,
  DEFAULT_ANALYSIS_TYPE_CONFIG,
  type AnalysisColor,
  type AnalysisTypeConfigItem,
  type AnalysisTypeKey,
} from "@/lib/constants/analysis";
import {
  DEFAULT_STATUS_ICON,
  STATUS_ICONS,
  type StatusIconConfigItem,
} from "@/lib/constants/status";
import type { LucideIcon } from "lucide-react";
import type { ComponentType, SVGProps } from "react";

// =============================================================================
// ANALYSIS VALUE UTILITIES
// =============================================================================

const UUID_REGEX =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/**
 * Determine analysis status from the analysis value.
 * If the value is a UUID, the analysis is completed (the UUID references the result).
 * Otherwise it is a status string like "pending", "processing", "in_progress".
 */
export function getAnalysisStatus(value: string | undefined): string {
  if (!value) return "";
  return UUID_REGEX.test(value) ? "completed" : value.toLowerCase();
}

// =============================================================================
// ANALYSIS TYPE UTILITIES
// =============================================================================

/**
 * Get analysis type configuration by matching the analysis type name.
 * Uses keyword matching to find the appropriate configuration.
 *
 * @param analysisTypeName - The name of the analysis type (e.g., "Image Analysis", "Sieve Analysis")
 * @returns The matching configuration or the default fallback
 *
 * @example
 * ```ts
 * const config = getAnalysisTypeConfig("Static Imaging Analysis");
 * // Returns: { key: "image_analysis", icon: CameraIcon, color: "purple", ... }
 *
 * const config = getAnalysisTypeConfig("ASTM C136 Sieve Analysis");
 * // Returns: { key: "gradation", icon: SievingIcon, color: "sky", ... }
 * ```
 */
export function getAnalysisTypeConfig(
  analysisTypeName: string | undefined | null,
): AnalysisTypeConfigItem {
  if (!analysisTypeName) {
    return { ...DEFAULT_ANALYSIS_TYPE_CONFIG };
  }

  // Normalize underscores to spaces so backend codes (e.g. "astm_c117") match keywords
  const normalizedName = analysisTypeName
    .toLowerCase()
    .trim()
    .replaceAll("_", " ");

  // Check for wash loss FIRST (more specific - contains both "wash" and related terms)
  // This prevents "sieve wash" from matching gradation instead of wash loss
  if (
    normalizedName.includes("wash loss") ||
    normalizedName.includes("astm c117") ||
    (normalizedName.includes("sieve") && normalizedName.includes("wash"))
  ) {
    return { ...ANALYSIS_TYPE_CONFIG.wash_loss };
  }

  // Check each analysis type's keywords
  for (const [key, config] of Object.entries(ANALYSIS_TYPE_CONFIG)) {
    if (key === "wash_loss") continue; // Already checked above

    for (const keyword of config.keywords) {
      if (normalizedName.includes(keyword.toLowerCase())) {
        return { ...config };
      }
    }
  }

  return { ...DEFAULT_ANALYSIS_TYPE_CONFIG };
}

/**
 * Get analysis type key from analysis type name.
 *
 * @param analysisTypeName - The name of the analysis type
 * @returns The analysis type key or "unknown"
 */
export function getAnalysisTypeKey(
  analysisTypeName: string | undefined | null,
): AnalysisTypeKey | "unknown" {
  const config = getAnalysisTypeConfig(analysisTypeName);
  return config.key;
}

/**
 * Get the icon component for an analysis type.
 *
 * @param analysisTypeName - The name of the analysis type
 * @returns The icon component
 */
export function getAnalysisTypeIcon(
  analysisTypeName: string | undefined | null,
): LucideIcon | ComponentType<SVGProps<SVGSVGElement>> {
  const config = getAnalysisTypeConfig(analysisTypeName);
  return config.icon;
}

/**
 * Get the color for an analysis type.
 *
 * @param analysisTypeName - The name of the analysis type
 * @returns The color key
 */
export function getAnalysisTypeColor(
  analysisTypeName: string | undefined | null,
): AnalysisColor {
  const config = getAnalysisTypeConfig(analysisTypeName);
  return config.color;
}

/**
 * Get Tailwind color classes for an analysis type.
 *
 * @param analysisTypeName - The name of the analysis type
 * @returns Object with bg, text, border, and iconBg Tailwind classes
 */
export function getAnalysisTypeColorClasses(
  analysisTypeName: string | undefined | null,
): {
  bg: string;
  text: string;
  border: string;
  iconBg: string;
} {
  const config = getAnalysisTypeConfig(analysisTypeName);
  return ANALYSIS_TYPE_COLORS[config.color];
}

// =============================================================================
// STATUS ICON UTILITIES
// =============================================================================

/**
 * Get status icon configuration by status string.
 *
 * @param status - The status string (e.g., "completed", "pending", "in_progress")
 * @returns The status icon configuration with icon component and animate flag
 *
 * @example
 * ```ts
 * const { icon: Icon, animate } = getStatusIconConfig("processing");
 * ```
 */
export function getStatusIconConfig(
  status: string | undefined | null,
): StatusIconConfigItem {
  if (!status) {
    return { ...DEFAULT_STATUS_ICON };
  }

  const normalizedStatus = status.toLowerCase().trim();
  const config = STATUS_ICONS[normalizedStatus as keyof typeof STATUS_ICONS];

  return config ? { ...config } : { ...DEFAULT_STATUS_ICON };
}

/**
 * Get the status icon component.
 *
 * @param status - The status string
 * @returns The icon component
 */
export function getStatusIcon(status: string | undefined | null): LucideIcon {
  const config = getStatusIconConfig(status);
  return config.icon;
}

/**
 * Check if status icon should be animated (e.g., spinning for processing).
 *
 * @param status - The status string
 * @returns Whether the icon should animate
 */
export function shouldAnimateStatusIcon(
  status: string | undefined | null,
): boolean {
  const config = getStatusIconConfig(status);
  return config.animate ?? false;
}
